import { useQuery } from '@tanstack/react-query';
import { hasPermission, hasRole } from '@delta/auth';
import type { AuthorizationSnapshot } from '@delta/domain';
import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import { api } from '../lib/api';
import { tenantSecureQueryKey } from '../shared/querySecurity';
import { useTenant } from '../tenant/TenantContext';
import { useAuth } from './AuthContext';

interface AuthorizationContextValue {
  snapshot: AuthorizationSnapshot | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
  has: (permissionCode: string) => boolean;
  isOwner: boolean;
}

const AuthorizationContext = createContext<AuthorizationContextValue | null>(null);

export function AuthorizationProvider({ children }: PropsWithChildren) {
  const tenant = useTenant();
  const { token, user } = useAuth();
  const query = useQuery({
    queryKey: tenantSecureQueryKey(
      tenant.id,
      user?.memberId ?? 'anonymous',
      'authorization',
    ),
    queryFn: async ({ signal }) => (await api.get<AuthorizationSnapshot>(
      '/iam/me/authorizations',
      { signal },
    )).data,
    enabled: Boolean(token && user),
    staleTime: 60_000,
    retry: false,
  });

  const value = useMemo<AuthorizationContextValue>(() => {
    return {
      snapshot: query.data ?? null,
      loading: Boolean(token && user) && query.isPending,
      error: Boolean(token && user) && query.isError,
      retry: () => { void query.refetch(); },
      has: (permissionCode) => hasPermission(query.data, permissionCode),
      isOwner: hasRole(query.data, 'TENANT_OWNER'),
    };
  }, [query.data, query.isError, query.isPending, query.refetch, token, user]);

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
}

export function useAuthorization() {
  const context = useContext(AuthorizationContext);
  if (!context) {
    throw new Error('useAuthorization 必须在 AuthorizationProvider 内使用');
  }
  return context;
}
